/**
 * RESOURCES — EVERY COMPANY DOCUMENT IN ONE PLACE.
 *
 * The founder, 14 September:
 *
 *   "we should probably add one last card to the Overview screen of the
 *    Portal: Resources. That's where every single company document can be
 *    found - SOPs, departmental Welcome Packs, HR resources, processes and
 *    procedures, etc."
 *
 *   "and then obviously remove the reference to these documents that appears
 *    at the top of each card's screen."
 *
 * ---------------------------------------------------------------------------
 * WHY THIS IS NOT A CARD IN admin_cards
 *
 * Every other destination in this portal is: `fn_my_admin_cards()` returns
 * them, a permission opens them, and lib/rail.js invents none of them. This one
 * is a fixed entry, and the reason is the table it reads.
 *
 * `portal_documents` is readable by every ACTIVE MEMBER OF STAFF and by no
 * permission at all — script 59's ruling, and it is still right: "gating them
 * behind a permission would mean a new joiner cannot read the document
 * explaining what they may do." A card in `admin_cards` is drawn only for
 * somebody holding a permission inside it, so making Resources a card would put
 * a lock on the outside of a room that is open — and the first person it would
 * shut out is the new joiner on their first morning, who holds nothing.
 *
 * So: the door is fixed, and the DATABASE still decides what comes through it.
 * `fn_portal_resources` checks `admin_users.status = 'active'` on every read
 * and returns an empty set to anybody else, which is what the suite proves
 * against a signed-in member of the public and against anon.
 *
 * ---------------------------------------------------------------------------
 * A MISSING DOCUMENT IS NAMED RATHER THAN OMITTED — FOR THE SLOTS ONLY
 *
 * This is what the card shelf did and it is worth keeping: two documents on a
 * shelf look complete until somebody goes hunting for the third, and the whole
 * value of promising an SOP behind every department is that its ABSENCE is
 * visible to the person who needs it.
 *
 * It applies to SLOT kinds and not to library kinds, which is the distinction
 * script 330 put in the database rather than in this file. Nobody promised
 * Finance an HR resource, so Finance is not missing one — and listing every
 * library kind as absent on every department would bury the real gap in
 * fourteen rows of noise.
 *
 * ---------------------------------------------------------------------------
 * AND THE PUBLISHER IS HERE, because without it this screen would be a window
 * onto a cabinet nobody can fill. `fn_publish_portal_document` has existed
 * since script 59 and was called by NO JAVASCRIPT ANYWHERE — every document on
 * any Orchid database today was inserted by hand. A read screen over that would
 * have been a complete, tested, unreachable subsystem, which this codebase
 * already has several of.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { O, SANS, Card, Field, StatusPill, Notice, Spinner, LoadingPanel, PrimaryCTA, QuietButton, TrustFooter, textInputStyle } from '../ui';
import {
  fetchPortalResources, fetchDocumentKinds, downloadUrlFor, publishDocument,
  groupResources, missingSlots,
} from '../lib/documents';
import { isMarkdown } from '../lib/markdown';
import MarkdownDoc from './MarkdownDoc';

const PUBLISH_PERMISSION = 'manage_staff_accounts';

const bytes = (n) => {
  if (typeof n !== 'number' || !Number.isFinite(n) || n <= 0) return null;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const GROUP_HEADING = {
  fontSize: 10.5, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase',
  color: O.inkFaint, margin: 0,
};

const rowButton = (busy) => ({
  padding: '9px 15px', borderRadius: 999,
  background: 'transparent', border: `1px solid ${O.hairlineOn}`,
  font: 'inherit', fontFamily: SANS, fontSize: 12.5, fontWeight: 600,
  cursor: busy ? 'default' : 'pointer',
  display: 'inline-flex', alignItems: 'center', gap: 8,
});

function DocumentRow({ document: doc, onOpen, onRead, busy, reading, readUrl, staff }) {
  const size = bytes(doc.size_bytes);
  const readable = isMarkdown(doc);
  return (
    <>
    <div data-document-row={doc.document_id} style={{
      display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between',
      gap: 12, padding: '12px 0', borderTop: `1px solid ${O.hairline}`,
    }}>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 13.5, fontWeight: 700, color: O.ink }}>{doc.title}</span>
          {doc.version > 1 && <StatusPill tone="quiet">Version {doc.version}</StatusPill>}
        </div>
        <p style={{ margin: '3px 0 0', fontSize: 11.5, lineHeight: 1.6, color: O.inkMuted }}>
          {doc.summary || doc.kind_blurb}
        </p>
        <p style={{ margin: '3px 0 0', fontSize: 11, color: O.inkFaint }}>
          {doc.kind_label}
          {doc.published_at
            ? ` · published ${new Date(doc.published_at).toLocaleDateString('en-ZA')}`
            : ''}
          {size ? ` · ${size}` : ''}
        </p>
      </div>
      {/* TWO BUTTONS, AND THEY SHARE A SIZE. A markdown document is read here;
          everything else is a file, and every document is still downloadable --
          the reader is an addition, never a door somebody has to go through. */}
      {/* A DOCUMENT THE PORTAL CAN RENDER IS READ HERE AND NOWHERE ELSE.
          The founder: "No company document openable in the Portal should be
          able to be downloaded." So the download is not offered beside the
          reader -- offering both would make the control a suggestion.

          IT IS REMOVED, NOT DISABLED, AND THAT DISTINCTION IS HONEST RATHER
          THAN COMPLETE: the bytes still reach the browser to be drawn, so
          anybody willing to open developer tools can still keep a copy. What
          this stops is the ordinary path and the accidental copy on a laptop
          that later leaves the company. What catches the deliberate one is the
          watermark and the read log, not this button.

          Anything the Portal CANNOT render still downloads, because a document
          nobody can open is not protected, it is lost. */}
      <div style={{ display: 'flex', flexShrink: 0, gap: 8 }}>
        <button
          type="button"
          onClick={() => (readable ? onRead(doc) : onOpen(doc))}
          disabled={busy}
          style={{ ...rowButton(busy), color: O.green }}
        >
          {busy && <Spinner size={13} />}
          {readable
            ? (reading ? 'Close' : 'Read')
            : (busy ? 'Preparing…' : 'Download')}
        </button>
      </div>
    </div>
    {reading && readUrl && (
      <div style={{
        padding: '4px 0 16px', borderTop: `1px solid ${O.hairline}`, marginTop: -1,
      }}>
        <MarkdownDoc url={readUrl} staff={staff} />
      </div>
    )}
    </>
  );
}

/**
 * Publishing, for whoever may.
 *
 * THE GATE IS THE PERMISSION AND NOT A ROLE (prohibition 5), and it is the same
 * key the database enforces — `fn_publish_portal_document` raises 42501 and the
 * storage policy refuses the upload regardless of what is drawn here. This
 * decides what is OFFERED; the database decides what is ALLOWED, twice.
 */
function Publisher({ kinds, cards, documents, onPublished }) {
  const [kind, setKind] = useState('');
  const [cardSlug, setCardSlug] = useState('');
  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [supersedes, setSupersedes] = useState('');
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState(null);
  const [message, setMessage] = useState(null);

  const chosen = kinds.find((k) => k.kind === kind) || null;
  const isCompanyPack = kind === 'company_welcome_pack';
  // A SLOT SUPERSEDES ITSELF AND NEEDS NO CHOOSING. The database works out
  // which document is in the slot; offering a "replaces" list for a slot kind
  // would be asking somebody to confirm a fact.
  const mayReplace = Boolean(chosen) && !chosen.is_slot;

  // What this could replace: the live documents of the same kind, in the same
  // scope. Anything else, the database refuses — "a hr_resource cannot replace
  // a sop", and a document that is already history cannot be replaced twice.
  const replaceable = useMemo(() => (documents || []).filter(
    (d) => d.kind === kind && (d.card_slug || '') === (cardSlug || ''),
  ), [documents, kind, cardSlug]);

  const submit = async () => {
    setBusy(true);
    setFailure(null);
    setMessage(null);
    const { data, error } = await publishDocument({
      file,
      kind,
      cardSlug: isCompanyPack ? null : (cardSlug || null),
      title,
      summary,
      supersedes: mayReplace ? (supersedes || null) : null,
    });
    setBusy(false);
    if (error) { setFailure(error); return; }
    setMessage(data?.message || 'Published.');
    setTitle('');
    setSummary('');
    setSupersedes('');
    setFile(null);
    onPublished();
  };

  return (
    <Card style={{ marginBottom: 22 }}>
      <h2 style={GROUP_HEADING}>Publish a document</h2>
      <p style={{ margin: '8px 0 0', fontSize: 12, lineHeight: 1.6, color: O.inkMuted }}>
        It appears here the moment it is published, for every member of staff. A new
        version of something that already exists does not overwrite it — the old one
        stays readable, because “which version applied on the day of this decision” is
        a question a regulator asks.
      </p>

      <div style={{ marginTop: 14, display: 'grid', gap: 12 }}>
        <Field label="What kind of document" htmlFor="pub-kind">
          <select
            id="pub-kind"
            value={kind}
            onChange={(e) => { setKind(e.target.value); setSupersedes(''); }}
            style={textInputStyle(false)}
          >
            <option value="">Choose…</option>
            {kinds.map((k) => (
              <option key={k.kind} value={k.kind}>{k.label}</option>
            ))}
          </select>
        </Field>

        {chosen && (
          <p style={{ margin: 0, fontSize: 11.5, lineHeight: 1.6, color: O.inkFaint }}>
            {chosen.blurb}
            {chosen.is_slot
              ? ' Publishing again replaces what is in this slot.'
              : ' There may be as many of these as there are.'}
          </p>
        )}

        {!isCompanyPack && (
          <Field
            label="Which department"
            hint={chosen && !chosen.is_slot
              ? 'Leave it on “The whole company” for something everybody needs.'
              : undefined}
            htmlFor="pub-card"
          >
            <select
              id="pub-card"
              value={cardSlug}
              onChange={(e) => { setCardSlug(e.target.value); setSupersedes(''); }}
              style={textInputStyle(false)}
            >
              <option value="">The whole company</option>
              {(cards || []).map((c) => (
                <option key={c.slug} value={c.slug}>{c.title}</option>
              ))}
            </select>
          </Field>
        )}

        <Field label="Title" htmlFor="pub-title">
          <input
            id="pub-title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            style={textInputStyle(false)}
          />
        </Field>

        <Field label="What it is for" hint="One line. Optional." htmlFor="pub-summary">
          <input
            id="pub-summary"
            type="text"
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            style={textInputStyle(false)}
          />
        </Field>

        {mayReplace && replaceable.length > 0 && (
          <Field
            label="Does it replace one of these?"
            hint="Leave it on “Nothing” and it is published alongside them."
            htmlFor="pub-supersedes"
          >
            <select
              id="pub-supersedes"
              value={supersedes}
              onChange={(e) => setSupersedes(e.target.value)}
              style={textInputStyle(false)}
            >
              <option value="">Nothing — this is a new document</option>
              {replaceable.map((d) => (
                <option key={d.document_id} value={d.document_id}>{d.title}</option>
              ))}
            </select>
          </Field>
        )}

        <Field label="The file" htmlFor="pub-file">
          <input
            id="pub-file"
            type="file"
            onChange={(e) => setFile((e.target.files || [])[0] || null)}
            style={{ ...textInputStyle(false), padding: 9 }}
          />
        </Field>
      </div>

      {failure && <div style={{ marginTop: 12 }}><Notice tone="critical">{failure}</Notice></div>}
      {message && <div style={{ marginTop: 12 }}><Notice tone="info">{message}</Notice></div>}

      <div style={{ marginTop: 14, maxWidth: 240 }}>
        <PrimaryCTA onClick={submit} busy={busy} disabled={busy || !file || !kind || !title.trim()}>
          {busy ? 'Publishing…' : 'Publish'}
        </PrimaryCTA>
      </div>
    </Card>
  );
}

export default function PortalResources({ cards = [], permissions = null, staff = null }) {
  const [rows, setRows] = useState(null);
  const [kinds, setKinds] = useState([]);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  // ONE DOCUMENT OPEN AT A TIME. Two long briefs expanded at once is a page
  // nobody can navigate, and the signed URL is minted per open so holding
  // several would mean holding several expiring links.
  const [readingId, setReadingId] = useState(null);
  const [readUrl, setReadUrl] = useState(null);
  const [openError, setOpenError] = useState(null);
  const [publishing, setPublishing] = useState(false);

  const load = useCallback(async () => {
    const [docs, ks] = await Promise.all([fetchPortalResources(), fetchDocumentKinds()]);
    if (docs.error) { setError(docs.error); setRows([]); } else { setError(null); setRows(docs.data); }
    // A failed kinds read is NOT an error on this screen. The documents are
    // what somebody came for; without the registry the shelf simply stops
    // naming what is missing, which is a smaller loss than a red page.
    setKinds(ks.error ? [] : ks.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const open = async (doc) => {
    setBusyId(doc.document_id);
    setOpenError(null);
    const { data: url, error: err } = await downloadUrlFor(doc.storage_path);
    setBusyId(null);
    if (err) { setOpenError(err); return; }
    // A signed URL is minted per click and expires. Opening it in a new tab
    // rather than navigating keeps the person on the screen they were in.
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const read = async (doc) => {
    if (readingId === doc.document_id) { setReadingId(null); setReadUrl(null); return; }
    setBusyId(doc.document_id);
    setOpenError(null);
    // THE SAME SIGNED URL THE DOWNLOAD USES. Reading in the Portal is a
    // different presentation of one private object, not a second access path --
    // the storage policy is what decides, exactly as it does for the tab.
    const { data: url, error: err } = await downloadUrlFor(doc.storage_path);
    setBusyId(null);
    if (err) { setOpenError(err); return; }
    setReadingId(doc.document_id);
    setReadUrl(url);
  };

  const mayPublish = (permissions || []).some((p) => p.permission_key === PUBLISH_PERMISSION);

  if (rows === null) return <LoadingPanel message="Opening Resources…" />;

  const groups = groupResources(rows);
  // Departments with nothing on them at all are still drawn, because an
  // empty shelf is what a department looks like before anybody writes its
  // procedure — and that is the thing worth seeing.
  const drawn = [
    ...groups,
    ...(cards || [])
      .filter((c) => !groups.some((g) => g.slug === c.slug))
      .map((c) => ({ key: c.slug, slug: c.slug, title: c.title, documents: [] })),
  ];
  const hasCompanyGroup = drawn.some((g) => g.slug === null);
  const withCompany = hasCompanyGroup
    ? drawn
    : [{ key: '__company__', slug: null, title: 'Company-wide', documents: [] }, ...drawn];

  return (
    <>
      {error && <Notice tone="critical">{error}</Notice>}
      {openError && <div style={{ marginTop: 12 }}><Notice tone="critical">{openError}</Notice></div>}

      <p style={{ fontSize: 12.5, lineHeight: 1.7, color: O.inkMuted, margin: '0 0 18px' }}>
        Every document Orchid holds for its staff: standard operating procedures, the
        welcome packs, HR resources, and the processes behind how things are done. They
        are readable by everybody who works here — you do not need a permission to open
        one. What is <strong>not</strong> here has not been written yet, and is named
        where it would have been.
      </p>

      <p style={{ fontSize: 12, lineHeight: 1.7, color: O.inkFaint, margin: '0 0 18px' }}>
        Documents the Portal can display are read here rather than downloaded, and carry
        your name while they are open. Some of what is on this shelf is internal only —
        treat a company document as something that does not leave the Portal.
      </p>

      {mayPublish && (
        <>
          <div style={{ marginBottom: 14 }}>
            <QuietButton onClick={() => setPublishing((v) => !v)}>
              {publishing ? 'Close' : 'Publish a document'}
            </QuietButton>
          </div>
          {publishing && (
            <Publisher
              kinds={kinds}
              cards={cards}
              documents={rows}
              onPublished={load}
            />
          )}
        </>
      )}

      <div style={{ display: 'grid', gap: 16 }}>
        {withCompany.map((group) => {
          const missing = missingSlots(kinds, group.documents, { companyWide: group.slug === null });
          return (
            <Card key={group.key}>
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
                <h2 style={GROUP_HEADING}>{group.title}</h2>
                {group.slug === null && <StatusPill tone="green">Everybody</StatusPill>}
              </div>

              {group.documents.length === 0 && missing.length === 0 && (
                <p style={{ margin: '10px 0 0', fontSize: 12, lineHeight: 1.6, color: O.inkFaint }}>
                  Nothing here yet.
                </p>
              )}

              {group.documents.map((doc) => (
                <DocumentRow
                  key={doc.document_id}
                  document={doc}
                  onOpen={open}
                  onRead={read}
                  busy={busyId === doc.document_id}
                  reading={readingId === doc.document_id}
                  readUrl={readingId === doc.document_id ? readUrl : null}
                  staff={staff}
                />
              ))}

              {missing.map((k) => (
                <div
                  key={k.kind}
                  style={{ padding: '12px 0', borderTop: `1px solid ${O.hairline}` }}
                >
                  <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: O.inkMuted }}>
                    {k.label}
                  </p>
                  <p style={{ margin: '3px 0 0', fontSize: 11.5, lineHeight: 1.6, color: O.inkFaint }}>
                    Not published yet. Human Capital publishes it, and it appears here when
                    they do.
                  </p>
                </div>
              ))}
            </Card>
          );
        })}
      </div>

      <TrustFooter />
    </>
  );
}
